import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ArticleCardDto, ArticleDto } from '@/ApiClient';
import { getInheritedTagIds } from '@/lib/article-tag-ids';
import { getMainOutPriceRangeForArticle } from '@lib/use-pricing-groups';
import { useActiveSeason } from '@/store/active-season';
import { entityCache } from '@/store/cache-primitives';

export const useArticleCards = defineStore('article-card', () => {
  const entities = entityCache<ArticleCardDto>();
  const articles = entities.data;
  const revision = ref(0);

  function update(article: ArticleCardDto) {
    entities.set(article);
    revision.value++;
  }

  function remove(articleId: number) {
    entities.remove([articleId]);
    revision.value++;
  }

  function deriveFromArticleDto(article: ArticleDto): ArticleCardDto {
    const mainOutPrice = getMainOutPriceRangeForArticle(article);
    // Resolve the article-season the card represents (active or latest non-base) so
    // article-level tag IDs match the BE ArticleCardDto.TagIds projection. Without this,
    // editing an article locally fans the cache update through here and re-renders the
    // card with the union of tags across all seasons.
    const resolvedSeasonality = useActiveSeason().activeOrLatestSeasonality(article.seasonalities);

    return {
      id: article.id,
      articleNumber: article.articleNumber,
      productName: article.product.name,
      productId: article.product.id,
      hierarchyNodeId: article.hierarchyNodeId,
      statusId: article.statusId,
      color: article.color
        ? { name: article.color.name, r: article.color.r, g: article.color.g, b: article.color.b }
        : { name: '', r: 0, g: 0, b: 0 },
      principalMaterialIds: resolvedSeasonality?.principalMaterialIds ?? [],
      genders: article.genders,
      tagIds: resolvedSeasonality?.tags.filter((t) => t.origin === 'articleSeason').map((t) => t.tagId) ?? [],
      productTagIds: getInheritedTagIds(article),
      seasonIds: article.seasonalities.filter((s) => s.id != null).map((s) => s.id!),
      image: article.images[0]?.url,
      imageFallback: article.product.images[0]?.url,
      mainOutPrice,
      orders: article.orders.map((o) => ({
        type: o.type?.name,
        quantity: o.quantity,
        statusId: o.statusId,
      })),
    };
  }

  // No tab-resume clearing: this is a pure per-id cache with no list/scroll state. The
  // board view's own resume handler reloads its buckets; keeping the card DTOs means the
  // cards stay visible during that reload instead of flashing skeletons.

  return { articles, revision, cache: entities.cache, update, remove, clear: entities.clear, deriveFromArticleDto };
});
